import { useEffect, useMemo, useState } from "react";
import { useElemContext } from "../Contexts/ElemContext";
import {
  addStudentsToGroup,
  deleteGroup,
  fetchGroup,
  fetchNoGroup,
  removeStudentsFromGroup,
} from "../services/groupService";
import { useFeedback } from "../Contexts/FeedbackContext";
import { ERROR_UNEXPECTED, ERROR_UPDATE } from "../constants/feedbacks";

function useGroupData(setGroups, setStudentNoGroup) {
  const { showFeedback } = useFeedback();

  useEffect(() => {
    fetchGroup()
      .then((res) => setGroups(res.data))
      .catch(() => showFeedback(ERROR_UNEXPECTED, false));

    fetchNoGroup()
      .then((res) => setStudentNoGroup(res.data))
      .catch(() => showFeedback(ERROR_UNEXPECTED, false));
  }, [setGroups, setStudentNoGroup, showFeedback]);
}

function useGroupActions(context) {
  const {
    selectedGroup,
    studentNoGroup,
    selectedStudentNoGroup,
    selectedStudentInGroup,
    groups,
    setGroups,
    setStudentNoGroup,
    setSelectedStudentNoGroup,
    setSelectedStudentInGroup,
  } = context;
  const { showFeedback } = useFeedback();

  const handleAdd = () => {
    if (!selectedGroup || selectedStudentNoGroup.length === 0) return;

    addStudentsToGroup(selectedGroup, selectedStudentNoGroup)
      .then(() => {
        const studentsToAdd = studentNoGroup.filter((student) =>
          selectedStudentNoGroup.includes(student.id)
        );

        setStudentNoGroup((prev) =>
          prev.filter((student) => !selectedStudentNoGroup.includes(student.id))
        );

        setGroups((prev) => {
          const updatedGroups = { ...prev };
          const groupKey = Object.keys(updatedGroups).find(
            (key) => updatedGroups[key].id === parseInt(selectedGroup, 10)
          );
          if (groupKey) {
            updatedGroups[groupKey].students = [
              ...updatedGroups[groupKey].students,
              ...studentsToAdd.filter(
                (newStudent) =>
                  !updatedGroups[groupKey].students.some(
                    (existing) => existing.id === newStudent.id
                  )
              ),
            ];
          }
          return updatedGroups;
        });

        setSelectedStudentNoGroup([]);
      })
      .catch(console.error);
  };

  const handleRemove = () => {
    if (!selectedGroup || selectedStudentInGroup.length === 0) return;

    removeStudentsFromGroup(selectedGroup, selectedStudentInGroup)
      .then(() => {
        const groupKey = Object.keys(groups).find(
          (key) => groups[key].id === parseInt(selectedGroup, 10)
        );
        const studentsToRemove = groups[groupKey].students.filter((student) =>
          selectedStudentInGroup.includes(student.id)
        );

        setGroups((prev) => ({
          ...prev,
          [groupKey]: {
            ...prev[groupKey],
            students: prev[groupKey].students.filter(
              (student) => !selectedStudentInGroup.includes(student.id)
            ),
          },
        }));

        setStudentNoGroup((prev) => [...prev, ...studentsToRemove]);
        setSelectedStudentInGroup([]);
      })
      .catch(console.error);
  };

  const handleDeleteGroup = (groupId) => {
    deleteGroup(groupId)
      .then(() => {
        const groupKey = Object.keys(groups).find(
          (key) => groups[key].id === parseInt(groupId, 10)
        );

        if (groupKey) {
          setStudentNoGroup((prev) => [...prev, ...groups[groupKey].students]);
          setGroups((prev) => {
            const updated = { ...prev };
            delete updated[groupKey];
            return updated;
          });
        }
      })
      .catch(() => {
        showFeedback(ERROR_UPDATE, false);
      });
  };

  return { handleAdd, handleRemove, handleDeleteGroup };
}

function useSortedStudents(
  studentNoGroup,
  searchTerm,
  order,
  groups,
  selectedGroup
) {
  const filteredStudents = useMemo(() => {
    if (!Array.isArray(studentNoGroup)) return [];
    const searchLower = searchTerm.toLowerCase();
    return studentNoGroup.filter((student) => {
      const fullName = `${student.name.toLowerCase()} ${student.surname.toLowerCase()}`;
      const reverseName = `${student.surname.toLowerCase()} ${student.name.toLowerCase()}`;
      return (
        fullName.includes(searchLower) || reverseName.includes(searchLower)
      );
    });
  }, [studentNoGroup, searchTerm]);

  const sortedStudentsNoGroup = useMemo(() => {
    return [...filteredStudents].sort((a, b) =>
      order === "name"
        ? a.name.localeCompare(b.name) || a.surname.localeCompare(b.surname)
        : a.surname.localeCompare(b.surname) || a.name.localeCompare(b.name)
    );
  }, [filteredStudents, order]);

  const sortedStudentsWithGroup = useMemo(() => {
    const currentGroup = Object.values(groups).find(
      (g) => g.id === Number(selectedGroup)
    );
    if (!currentGroup) return [];
    return [...currentGroup.students].sort(
      (a, b) =>
        a.surname.localeCompare(b.surname) || a.name.localeCompare(b.name)
    );
  }, [groups, selectedGroup]);

  const sortedGroups = useMemo(() => {
    if (!groups) return {};
    return Object.keys(groups)
      .sort()
      .reduce((acc, key) => {
        acc[key] = groups[key];
        return acc;
      }, {});
  }, [groups]);

  return { sortedStudentsNoGroup, sortedStudentsWithGroup, sortedGroups };
}

export function useGroupManager() {
  const context = useElemContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [order, setOrder] = useState("surname");

  useGroupData(context.setGroups, context.setStudentNoGroup);

  const { handleAdd, handleRemove, handleDeleteGroup } =
    useGroupActions(context);

  const { sortedStudentsNoGroup, sortedStudentsWithGroup, sortedGroups } =
    useSortedStudents(
      context.studentNoGroup,
      searchTerm,
      order,
      context.groups,
      context.selectedGroup
    );

  return {
    ...context,
    handleAdd,
    handleRemove,
    handleDeleteGroup,
    searchTerm,
    setSearchTerm,
    order,
    setOrder,
    sortedStudentsNoGroup,
    sortedStudentsWithGroup,
    sortedGroups,
  };
}
